import { useEffect, useState } from 'react';
import api, { API_BASE } from '@/lib/api';
import { PageHeader, EmptyState } from '@/components/ui-bits';
import { FormInput, Plus, X, Save, Trash2, Copy, Code } from 'lucide-react';
import { toast } from 'sonner';

export default function FormsPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => { const { data } = await api.get('/forms'); setItems(data.forms); };
  useEffect(() => { load(); }, []);
  const newForm = () => setEditing({
    name: 'Landing principal', enabled: true, auto_tag: 'landing',
    fields: [{ key: 'nombre', label: 'Nombre', type: 'text', required: true }, { key: 'telefono', label: 'Teléfono', type: 'phone', required: true }, { key: 'email', label: 'Email', type: 'email' }],
    success_message: '¡Gracias! Te contactaremos pronto.',
  });
  const save = async () => {
    try {
      if (editing.id) await api.patch(`/forms/${editing.id}`, editing);
      else await api.post('/forms', editing);
      toast.success('Guardado'); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  const del = async (f) => { if (!confirm('Borrar?')) return; await api.delete(`/forms/${f.id}`); load(); };
  const copyEmbed = (f) => {
    const code = `<script>
  async function submitZunoid(form) {
    const data = Object.fromEntries(new FormData(form));
    const r = await fetch('${API_BASE}/public/forms/${f.public_id}/submit', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(data) });
    return r.json();
  }
</script>
<form onsubmit="event.preventDefault(); submitZunoid(this).then(r=>alert(r.success_message||'OK'))">
${f.fields.map((x) => `  <label>${x.label}<input name="${x.key}" ${x.required ? 'required' : ''} type="${x.type === 'phone' ? 'tel' : x.type}" /></label>`).join('\n')}
  <button type="submit">Enviar</button>
</form>`;
    navigator.clipboard.writeText(code); toast.success('Código copiado');
  };

  return (
    <div>
      <PageHeader title="Formularios" description="Constructores de formularios para landings." actions={<button data-testid="form-new" onClick={newForm} className="btn-primary"><Plus className="h-4 w-4" /> Nuevo form</button>} />
      {items.length === 0 ? (
        <EmptyState icon={FormInput} title="Sin formularios" description="Crea tu primer formulario y obtén el snippet embed." cta={<button onClick={newForm} className="btn-primary">Crear</button>} />
      ) : (
        <div className="grid lg:grid-cols-2 gap-3">
          {items.map((f) => (
            <div key={f.id} className="surface p-4">
              <div className="flex items-center justify-between">
                <div><div className="font-medium">{f.name}</div><div className="text-xs text-muted-foreground">{f.fields?.length || 0} campos · {f.enabled ? 'activa' : 'inactiva'}</div></div>
                <div className="flex gap-1"><button onClick={() => copyEmbed(f)} className="btn-ghost h-8 px-2 text-xs"><Code className="h-3 w-3" /> Embed</button><button onClick={() => setEditing(f)} className="btn-ghost h-8 px-2 text-xs">Editar</button><button onClick={() => del(f)} className="btn-ghost h-8 w-8 p-0 text-[hsl(var(--danger))]"><Trash2 className="h-4 w-4" /></button></div>
              </div>
              <div className="mt-2 text-xs font-mono text-muted-foreground">Public ID: {f.public_id}</div>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4"><h2 className="font-display text-xl font-semibold">Formulario</h2><button onClick={() => setEditing(null)} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button></div>
            <div className="space-y-3">
              <label>Nombre<input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input mt-1" /></label>
              <label>Tag automático<input value={editing.auto_tag || ''} onChange={(e) => setEditing({ ...editing, auto_tag: e.target.value })} className="input mt-1" /></label>
              <label>Mensaje de éxito<input value={editing.success_message || ''} onChange={(e) => setEditing({ ...editing, success_message: e.target.value })} className="input mt-1" /></label>
              <div>
                <div className="flex items-center justify-between"><span className="text-sm font-medium">Campos</span><button onClick={() => setEditing({ ...editing, fields: [...editing.fields, { key: '', label: '', type: 'text' }] })} className="btn-ghost h-7 px-2 text-xs"><Plus className="h-3 w-3" /> Agregar</button></div>
                {editing.fields.map((fld, i) => (
                  <div key={i} className="grid grid-cols-4 gap-2 mt-2">
                    <input value={fld.label} onChange={(e) => { const a = [...editing.fields]; a[i] = { ...a[i], label: e.target.value }; setEditing({ ...editing, fields: a }); }} placeholder="Etiqueta" className="input" />
                    <input value={fld.key} onChange={(e) => { const a = [...editing.fields]; a[i] = { ...a[i], key: e.target.value.replace(/\s+/g, '_').toLowerCase() }; setEditing({ ...editing, fields: a }); }} placeholder="key" className="input font-mono text-xs" />
                    <select value={fld.type} onChange={(e) => { const a = [...editing.fields]; a[i] = { ...a[i], type: e.target.value }; setEditing({ ...editing, fields: a }); }} className="input">{['text', 'email', 'phone', 'number', 'select'].map((t) => <option key={t}>{t}</option>)}</select>
                    <button onClick={() => setEditing({ ...editing, fields: editing.fields.filter((_, ix) => ix !== i) })} className="btn-ghost h-9 w-9 p-0 text-[hsl(var(--danger))]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4"><button onClick={() => setEditing(null)} className="btn-ghost">Cancelar</button><button data-testid="form-save" onClick={save} className="btn-primary"><Save className="h-4 w-4" /> Guardar</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
